import React, { forwardRef, useCallback } from 'react';
import { FileError, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { COMMON_TRANSLATE_KEYS } from '../../../constants/translate-keys';
import ImageCarousel from '../../carousel/ImageCarousel';
import { TImage } from '../../../types';

interface DropzoneProps {
  addImages: (images: TImage[]) => void;
  removeImage: (image: TImage) => void;
  images?: Array<TImage>;
  showFullImageOnClick?: boolean;
  containerWidth?: string;
  customDropzoneStyle?: React.CSSProperties;
  maxFiles?: number;
}

// Previously used
export interface DropzoneImperativeHandle {
  clearImages: () => void;
}

const showDuplicateFileToast = (msg: string) => {
  toast(msg, {
    toastId: 'duplicate-file',
  });
};

const Dropzone: React.FC<DropzoneProps> = forwardRef(
  (
    {
      addImages,
      removeImage,
      images = [],
      showFullImageOnClick,
      customDropzoneStyle = {},
      maxFiles = 100,
      containerWidth = '37rem',
    },
    ref
  ) => {
    const { t } = useTranslation();

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
          let duplicateFiles = 0;

          for (let index = 0; index < acceptedFiles.length; index++) {
            const currentFile = acceptedFiles[index];
            const fileExists = images.find(
              (img) => img.id === currentFile.name
            );

            if (fileExists) {
              duplicateFiles++;
              continue;
            }

            const reader = new FileReader();

            reader.onload = () => {
              const imageData = reader.result as string;

              const image: TImage = {
                id: currentFile.name,
                src: imageData,
                file: currentFile,
              };

              addImages([image]);
            };

            reader.readAsDataURL(currentFile);
          }

          if (duplicateFiles > 0) {
            showDuplicateFileToast(
              t(COMMON_TRANSLATE_KEYS.DUPLICATE_FILE, { count: duplicateFiles })
            );
          }
        }
      },
      [addImages, images, t]
    );

    const onImageRemove = useCallback(
      (image: TImage) => {
        removeImage(image);
      },
      [removeImage]
    );

    const mapError = (err: FileError) => {
      switch (err.code) {
        case 'file-invalid-type':
          return t(COMMON_TRANSLATE_KEYS.ALLOWED_FILE_TYPES, {
            fileTypes: '.jpg, .jpeg, .png',
          });
        case 'too-many-files':
          return t(COMMON_TRANSLATE_KEYS.MAX_FILE_LIMIT, { limit: maxFiles });
      }
      return err.code;
    };

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragReject,
      fileRejections,
    } = useDropzone({
      onDrop,
      accept: {
        'image/jpeg': [],
        'image/png': [],
      },
      maxSize: 10485760, // 10MB
      multiple: true,
      maxFiles,
    });

    const dropzoneStyle = {
      height: '300px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      ...customDropzoneStyle,
    };

    return (
      <>
        <div style={{ width: containerWidth }}>
          <div
            {...getRootProps()}
            style={dropzoneStyle}
            className="bg-backGroundGray w-full sm:text-start text-center px-3"
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>{t(COMMON_TRANSLATE_KEYS.DROP_HERE)}</p>
            ) : isDragReject ? (
              <p>{t(COMMON_TRANSLATE_KEYS.UPLOAD_FAILED)}</p>
            ) : (
              <p>{t(COMMON_TRANSLATE_KEYS.DRAG_AND_DROP)}</p>
            )}
          </div>
          <p className="text-functionalRed">
            {fileRejections &&
              fileRejections.length > 0 &&
              fileRejections[0].errors.map((err) => mapError(err))}
          </p>
          {images.length > 0 && (
            <ImageCarousel
              onRemoveItem={onImageRemove}
              images={images}
              itemClassName="w-full max-w-[594px] sm:h-[200px] h-[300px] p-5"
              showFullImageOnClick={showFullImageOnClick}
            />
          )}
        </div>
      </>
    );
  }
);

export default Dropzone;
